/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { convertToRem } from '../../utils/pixel-to-rem-convertor.js';

/**
 * @prop {String} label - Defines the button's label in all shapes (in circle shape it will only be shown if tooltip is also defined).
 * @prop {String} color - Defines the color of the button. Possible values are `primary`, `secondary`, `tertiary`.
 * @prop {String} size - If icon is not set, defines the size of the button. Possible values are `s`, `m`, `l`.
 * @prop {Boolean} disabled - If set, won't allow mouse events and disabled styles will be applied to the button.
 * @prop {String} href - Defines a path to which navigation will occur on click.
 * @prop {String} tooltip - Defines the position of the tooltip (if an icon and label are set).
 * @prop {String} href - Defines a path to which navigation will occur on click.
 * @prop {Boolean} arrowDisabled - Disables split button arrow.
 * @prop {String} role - *hide
 */
export class SHSplitButton extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      size: { type: String, reflect: true },
      color: { type: String, reflect: true },
      tooltip: { type: String, reflect: true },
      href: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
      role: { type: String, reflect: true },
      arrowDisabled: {
        type: Boolean,
        reflect: true,
        attribute: 'arrow-disabled',
      },
      _active: { type: Boolean },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: flex;
          flex-shrink: 0;
          flex-direction: row;
          height: 2rem;
          width: fit-content;
          width: -moz-fit-content;
        }
        sh-button {
          border-top-right-radius: 0rem;
          border-bottom-right-radius: 0rem;
        }
        .icon-wrapper {
          box-sizing: border-box;
          width: 2rem;
          height: 2rem;
          border-top-right-radius: 1rem;
          border-bottom-right-radius: 1rem;
          cursor: pointer;
        }
        .split-button-icon {
          margin-left: -0.125rem;
        }
        /* color='primary' */
        :host([color='primary']) .icon-wrapper {
          background-color: rgba(var(--ui-3), var(--opacity-1));
          border-left: 0.0625rem solid rgba(var(--ui-7), var(--opacity-5));
        }
        :host([color='primary']) .icon-wrapper[active] {
          background-color: rgba(var(--ui-4), var(--opacity-1));
        }
        :host([color='primary']) .icon-wrapper > sh-icon {
          color: rgba(255, 255, 255, 1);
        }
        :host([color='primary']:not(.touch-device):not([arrow-disabled]))
          .icon-wrapper:hover:not([active]) {
          background-color: rgba(var(--ui-2), var(--opacity-1));
        }
        /* color='secondary' */
        :host([color='secondary']) .icon-wrapper {
          background-color: rgba(var(--ui-1), var(--opacity-6));
          border-left: 0.0625rem solid rgba(var(--ui-7), var(--opacity-4));
        }
        :host([color='secondary']) .icon-wrapper[active] {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        :host([color='secondary']) .icon-wrapper > sh-icon {
          color: rgba(var(--ui-1), var(--opacity-2));
        }
        :host([color='secondary']:not(.touch-device):not([arrow-disabled]))
          .icon-wrapper:hover:not([active]) {
          background-color: rgba(var(--ui-1), var(--opacity-5));
        }
        /* color='tertiary' */
        :host([color='tertiary']) .icon-wrapper {
          background-color: transparent;
          border: 0.0625rem solid rgba(var(--ui-1), var(--opacity-6));
          border-left: none;
        }
        :host([color='tertiary']) .icon-wrapper[active] {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        :host([color='tertiary']) .icon-wrapper > sh-icon {
          color: rgba(var(--ui-1), var(--opacity-2));
        }
        :host([color='tertiary']:not(.touch-device):not([arrow-disabled]))
          .icon-wrapper:hover:not([active]) {
          background-color: rgba(var(--ui-1), var(--opacity-5));
        }
        /* disabled */
        :host([disabled]),
        :host([arrow-disabled]) .icon-wrapper {
          pointer-events: none;
          cursor: default;
        }
        :host([color='primary'][disabled]) .icon-wrapper {
          background-color: rgba(var(--ui-2), var(--opacity-5));
        }
        :host([color='secondary'][disabled]) .icon-wrapper {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        :host([color='tertiary'][disabled]) .icon-wrapper {
          border: 0.0625rem solid rgba(var(--ui-1), var(--opacity-7));
          border-left: none;
        }
      `,
    ];
  }
  render() {
    return html`
      <sh-button
        label="${this.label}"
        size="${this.size}"
        color="${this.color}"
        .tooltip="${this.tooltip}"
        .href="${this.href}"
        ?disabled="${this.disabled}"
        role="${this.role}"
      >
      </sh-button>
      <div class="icon-wrapper" ?active="${this._active}" @click="${this._toggleOverlay}">
        <sh-icon
          class="split-button-icon"
          icon="down-s"
          tabindex="0"
          ?disabled="${this.disabled || this.arrowDisabled}"
        >
        </sh-icon>
      </div>
      <sh-popover ?visible="${this._active}">
        <slot></slot>
      </sh-popover>
    `;
  }
  constructor() {
    super();
    this.label = 'Label';
    this.size = 's';
    this.color = 'primary';
    this.role = 'button';
  }
  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    this.documentClick = this._handleClick.bind(this);
    document.body.addEventListener('click', this.documentClick);
  }

  _toggleOverlay(e) {
    e.stopImmediatePropagation();
    if (this._active) {
      this._active = false;
      this._hideOverlay();
    } else {
      this._active = true;
      this._showOverlay();
    }
  }

  _handleClick() {
    this._active = false;
    this._hideOverlay();
  }

  _showOverlay() {
    if (this.overlayHandler) {
      window.cancelAnimationFrame(this.overlayHandler);
    }
    this.overlayHandler = window.requestAnimationFrame(this._keepShowing.bind(this));
  }

  _hideOverlay() {
    if (this.overlayHandler) {
      window.cancelAnimationFrame(this.overlayHandler);
    }
  }

  _keepShowing() {
    if (this.overlayHandler) {
      window.cancelAnimationFrame(this.overlayHandler);
    }
    this._show();
    this.overlayHandler = window.requestAnimationFrame(this._keepShowing.bind(this));
  }

  _show() {
    const popover = this.shadowRoot.querySelector('sh-popover');
    const popoverRect = popover.getBoundingClientRect();
    const targetRect = this.getBoundingClientRect();
    const offset = 8;
    popover.visible = true;

    /**
     * Vertical positioning of popover based on available space
     */
    if (targetRect.bottom + popoverRect.height + offset < window.innerHeight) {
      popover.style.top = `${convertToRem(targetRect.bottom + offset)}rem`;
    } else {
      popover.style.top = `${convertToRem(targetRect.top - popoverRect.height - offset)}rem`;
    }

    /**
     * Horizontal positioning of popover based on available space
     */
    if (window.innerWidth - targetRect.right < popoverRect.width - targetRect.width) {
      popover.style.right = `${convertToRem(window.innerWidth - targetRect.right)}rem`;
    }
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.overlayHandler);
    document.body.removeEventListener('click', this.documentClick);
    super.disconnectedCallback();
  }
}
customElements.define('sh-split-button', SHSplitButton);
